import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, StatusBar, StyleSheet } from 'react-native';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useOnEscPress from 'src/hooks/key-press/useOnEscPress';
import useOnBackPress from 'src/hooks/useOnBackPress';
import styleUtils from 'src/utils/styles';
import Column from '../layout/Column';
import ColumnCenter from '../layout/ColumnCenter';
import Pressable from '../pressables/Pressable';
import { getSlidingAnimationStyle, getSlidingContainerStyle } from './utils';
const AnimationDuration = 300;
const Overlay = ({ visible, onClose, direction = 'from-bottom', children, style, ...columnProps }) => {
    const [{ styles, theme, dimensions, deviceType }] = useTheme(stylesGenerator, { direction }, [direction]);
    const [hide, setHide] = useState(true);
    const animation = useRef(new Animated.Value(0)).current;
    const [showComponent, setShowComponent] = useState(false);
    const disableBackdropPressToClose = deviceType.isDesktop && direction === 'from-bottom';
    useOnBackPress(useCallback(() => {
        if (!visible) {
            return false;
        }
        onClose();
        return true;
    }, [onClose, hide]));
    useEffect(() => {
        if (visible) {
            setHide(false);
            Animated.timing(animation, {
                toValue: 1,
                duration: AnimationDuration,
                useNativeDriver: false,
                easing: Easing.out(Easing.ease),
            }).start(() => {
                setShowComponent(true);
            });
        }
        else {
            Animated.timing(animation, {
                toValue: 0,
                duration: AnimationDuration,
                useNativeDriver: false,
                easing: Easing.out(Easing.ease),
            }).start(() => {
                setHide(true);
                setShowComponent(false);
            });
        }
        return () => {
            animation.stopAnimation();
        };
    }, [visible]);
    useOnEscPress(onClose);
    const bgAnimation = animation.interpolate({
        inputRange: [0, 1],
        outputRange: ['rgba(0,0,0,0)', theme.palette.backdrop.main],
        extrapolate: 'clamp',
    });
    const renderContent = () => (React.createElement(ColumnCenter, { flex: true },
        React.createElement(Column, { flex: true, sab: true, style: [styles.contentContainer, style], ...columnProps }, showComponent && children)));
    const renderContentWithPressable = () => (React.createElement(Pressable, { style: [
            styleUtils.flex,
            ...(deviceType.isDesktop ? [styleUtils.justifyCenter, styleUtils.alignCenter] : [])
        ], onPress: onClose },
        React.createElement(Pressable, { onPress: e => e.stopPropagation(), flex: true, style: [styles.contentContainer, style], ...columnProps },
            React.createElement(Column, { flex: true, sab: true }, showComponent && children))));
    return !hide ? (React.createElement(Animated.View, { style: [
            StyleSheet.absoluteFill,
            styles.bgContainer,
            {
                backgroundColor: bgAnimation,
            },
        ] },
        React.createElement(StatusBar, { backgroundColor: theme.palette.backdrop.main, barStyle: theme.themeID === 'light' ? 'light-content' : 'dark-content' }),
        React.createElement(Animated.View, { style: [
                styleUtils.containerHeight,
                styleUtils.containerWidth,
                getSlidingAnimationStyle(animation, dimensions, direction),
            ] }, disableBackdropPressToClose ? renderContent() : renderContentWithPressable()))) : null;
};
const stylesGenerator = createThemedStyle((ctx, { direction }) => ({
    bgContainer: {
        backgroundColor: ctx.theme.palette.backdrop.main,
    },
    contentContainer: {
        position: 'absolute',
        ...getSlidingContainerStyle(ctx, direction),
        backgroundColor: ctx.theme.palette.background.main,
        overflow: 'hidden',
    },
}));
export default Overlay;
